import isNumber from 'lodash/isNumber'
import merge from 'lodash/merge'
import invariant from 'invariant'
import cleanupRemWhenOnSite from '@signal/theme/utils/cleanupRemWhenOnSite'
import { formatTypography, formatMuiTypography } from './utils'

/**
 * Middleware that handles `typography` and `muiTypography` key overrides.
 * @type {ThemeMiddleware}
 */
export function typographyMiddleware(baseOverrides, customOverrides) {
  const {
    typography: baseTypography = {},
    muiTypography: baseMuiTypography = {},
    ...restBaseOverrides
  } = baseOverrides
  const {
    typography: inputTypography = {},
    muiTypography: inputMuiTypography = {},
    ...restInputOverrides
  } = customOverrides

  // Merge everything except for typography overrides
  const overrides = merge({}, restBaseOverrides, restInputOverrides)
  const {
    constants: { HTML_FONT_SIZE, FONT_SIZE, FONT_FAMILY }
  } = overrides

  function pxToRem(value) {
    const coef = FONT_SIZE / 14
    return cleanupRemWhenOnSite(`${(value / HTML_FONT_SIZE) * coef}rem`)()
  }

  invariant(
    isNumber(HTML_FONT_SIZE) && isNumber(FONT_SIZE),
    'Function `createTypography` expected both `HTML_FONT_SIZE` and `FONT_SIZE` to be numbers.'
  )

  // For both standard typography overrides and muiTypography overrides map them
  // with their respective format function and merge base with input.
  const typography = merge(
    {},
    ...[baseTypography, inputTypography].map(typography =>
      formatTypography(typography, overrides, pxToRem)
    )
  )
  const muiTypography = merge(
    {},
    ...[baseMuiTypography, inputMuiTypography].map(typography =>
      formatMuiTypography(typography, overrides)
    )
  )

  return {
    ...customOverrides,
    typography: {
      fontSize: FONT_SIZE,
      htmlFontSize: HTML_FONT_SIZE,
      fontFamily: FONT_FAMILY,
      ...typography,
      ...muiTypography
    }
  }
}

export default typographyMiddleware
